// components/GalleryImagePicker.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Spin, notification, Pagination } from 'antd';
import { ancanaApi, ancanaApiHeaders } from "../../api/ancanaApi";
import DraggableUpload from "../DraggableUpload";
import { PlusOutlined } from "@ant-design/icons";

interface GalleryImagePickerProps {
  visible: boolean;
  onSelect: (imageUrl: string) => void;
  onCancel: () => void;
  galleryId?: number;
  selectSize?: "thumbnail" | "hd" | "fhd" | "image" | "pdf_optimized";
}

const BLOG_MEDIA_GALLERY_ID = 218;
const baseImgPath = 'gallery-images';
const PAGE_SIZE = 35;

function GalleryImagePicker({ visible, onSelect, onCancel, galleryId, selectSize = "hd" }: GalleryImagePickerProps) {
  const [fileList, setFileList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const GAL_ID = galleryId || BLOG_MEDIA_GALLERY_ID;

  const fetchImages = useCallback((pageNum: number) => {
    setLoading(true);
    ancanaApi.get(`gallery-images/?gallery=${GAL_ID}&page_size=${PAGE_SIZE}&page=${pageNum}&ordering=-created_at`)
      .then(res => {
        const fetchedImages = res.data.results.sort((a: any, b: any) => a.sort_index - b.sort_index);
        const newFiles = fetchedImages.map((img: any) => ({
          uid: img.id,
          name: img.name,
          status: 'done',
          url: img.thumbnail,
          hd: img.hd,
          fhd: img.fhd,
          image: img.image,
        }));
        setTotal(res.data.count);
        setFileList(current => {
          const uploading = current.filter(f => f.status === 'uploading');
          return [...newFiles, ...uploading];
        });
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading images:', err);
        setLoading(false);
      });
  }, [GAL_ID]);

  useEffect(() => {
    if (visible) {
      setPage(1);
      fetchImages(1);
    } else {
      setFileList([]);
      setPage(1);
      setTotal(0);
    }
  }, [visible, fetchImages]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchImages(newPage);
  };

  const handleOnChange = ({ file, fileList: newFileList }: any) => {
    setFileList(newFileList);
    if (file.status === 'done') {
      notification.success({ message: `Image "${file.name}" uploaded successfully` });
      setPage(1);
      fetchImages(1);
    } else if (file.status === 'error') {
      notification.error({ message: `Image "${file.name}" upload failed.` });
    }
  };

  const handleRemove = (file: any) => {
    return new Promise<boolean>((resolve, reject) => {
      Modal.confirm({
        title: 'Delete image?',
        content: 'This action cannot be undone.',
        onOk: () => {
          ancanaApi.delete(`${baseImgPath}/${file.uid}/`).then(() => {
            notification.success({ message: 'Image deleted' });
            setPage(1);
            fetchImages(1);
            resolve(true);
          }).catch(() => {
            notification.error({ message: 'Failed to delete image' });
            reject(false);
          });
        },
        onCancel: () => reject(false),
      });
    });
  };

  return (
    <Modal
      title="Select Image"
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={832}
      destroyOnClose
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 12 }}>Loading images...</div>
        </div>
      ) : (
        <>
          <DraggableUpload
            multiple
            action={`${process.env.REACT_APP_API_URL}/v1/${baseImgPath}/`}
            headers={ancanaApiHeaders}
            data={(file) => ({ image: file, gallery: GAL_ID })}
            listType="picture-card"
            fileList={fileList}
            onChange={handleOnChange}
            onPreview={(file: any) => {
              const imageUrl = file[selectSize] || file.response[selectSize];
              if (imageUrl) onSelect(imageUrl);
            }}
            onSort={() => {}}
            onRemove={handleRemove}
          >
            <div>
              <PlusOutlined />
              <div style={{ marginTop: 8 }}>Upload</div>
            </div>
          </DraggableUpload>
          {total > PAGE_SIZE && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Pagination
                current={page}
                pageSize={PAGE_SIZE}
                total={total}
                onChange={handlePageChange}
                showSizeChanger={false}
              />
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

export default GalleryImagePicker;
